import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Timer, Gift, Search, Map, FileText } from "lucide-react";
import { Reveal } from "@/components/od/Reveal";
import { MaskedLine } from "@/components/od/Hero";
import { useSimplify } from "@/components/od/simplify-context";

const SITE = "https://oceandata.ca";

const MINUTES = [
  {
    range: "Minutes 0 à 3",
    icon: Search,
    title: "Vous décrivez une semaine ordinaire",
    body: "Pas de présentation, pas de jargon. Vous racontez comment le travail circule : les suivis, les ressaisies, les rapports montés à la main. On écoute.",
  },
  {
    range: "Minutes 3 à 10",
    icon: Map,
    title: "On repère où les heures se cachent",
    body: "Ensemble, on trace le trajet réel d'un processus — du client au paiement — et on pointe les fuites : la chasse, la ressaisie, la recherche, la réunion de trop.",
  },
  {
    range: "Minutes 10 à 15",
    icon: FileText,
    title: "Vous repartez avec la première simplification",
    body: "Les deux ou trois endroits où commencer, dans l'ordre, avec ce que chacun rapporterait en heures. Utile que vous travailliez avec nous ou non.",
  },
];

const FAQ = [
  {
    q: "Pourquoi c'est gratuit ?",
    a: "Parce que 15 minutes de franchise valent plus qu'une heure de démo. Si on peut vous aider, vous le saurez vite. Si on ne peut pas, on vous le dira — et vous garderez quand même la carte de vos fuites.",
  },
  {
    q: "Est-ce que c'est un appel de vente déguisé ?",
    a: "Non. Aucune séquence de relance, aucune pression. Vous parlez à Bruno directement — 647 implémentations depuis 2017 — et la décision de continuer vous appartient entièrement.",
  },
  {
    q: "Qu'est-ce qui se passe après, si ça clique ?",
    a: "Un seul processus, à prix fixe, précédé de 30 jours d'essai gratuit sur vos vraies données. À la fin, un rapport mesuré des heures récupérées — que vous gardez dans tous les cas.",
  },
  {
    q: "Pour qui, exactement ?",
    a: "Les entreprises de construction et de services professionnels de 10 à 100 personnes, à Montréal et partout au Québec, dont les outils ne se parlent pas : Excel, QuickBooks, Microsoft 365, Google, Zoho, Autodesk.",
  },
];

export default function AuditGratuitPage() {
  const { setOpen } = useSimplify();

  useEffect(() => {
    document.title = "L'audit gratuit de 15 minutes | Ocean Data — Montréal";
    document.documentElement.lang = "fr";
    const el = document.createElement("link");
    el.rel = "alternate";
    el.hreflang = "fr-CA";
    el.href = `${SITE}/fr/audit-gratuit-15-minutes`;
    el.setAttribute("data-hreflang-injected", "true");
    document.head.appendChild(el);
    return () => {
      document.documentElement.lang = "en";
      el.remove();
    };
  }, []);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": `${SITE}/fr/audit-gratuit-15-minutes`,
        name: "L'audit gratuit de 15 minutes",
        description:
          "Quinze minutes, vos chiffres, une réponse honnête. Ocean Data repère les deux ou trois endroits où votre entreprise de construction ou de services professionnels pourrait simplifier d'abord. Montréal et tout le Québec.",
        inLanguage: "fr-CA",
        isPartOf: { "@id": `${SITE}/#website` },
        about: { "@id": `${SITE}/#org` },
      },
      {
        "@type": "Service",
        name: "Revue d'affaires gratuite de 15 minutes",
        provider: { "@id": `${SITE}/#org` },
        areaServed: [
          { "@type": "City", name: "Montréal" },
          { "@type": "AdministrativeArea", name: "Québec" },
        ],
        description:
          "Une revue gratuite et sans pression de là où vont les heures de votre équipe — le point de départ de chaque mandat Ocean Data.",
        offers: { "@type": "Offer", price: "0", priceCurrency: "CAD" },
      },
      {
        "@type": "FAQPage",
        inLanguage: "fr-CA",
        mainEntity: FAQ.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
    ],
  };

  return (
    <main className="pt-16 sm:pt-20 bg-[#F8FAFC]" lang="fr">
      {/* Hero */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-6">
            Montréal · Québec · Sans engagement
          </p>
          <h1
            data-testid="audit-headline"
            className="font-display font-bold tracking-tight leading-[1.06] text-brand-navy text-4xl sm:text-5xl lg:text-6xl max-w-3xl"
          >
            <MaskedLine delay={0.15} testId="audit-headline-line-1">L'audit gratuit</MaskedLine>
            <MaskedLine delay={0.3} testId="audit-headline-line-2">
              <span className="text-brand-teal">de 15 minutes.</span>
            </MaskedLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="mt-7 max-w-xl text-base sm:text-lg text-slate-600 leading-relaxed"
          >
            Quinze minutes, vos chiffres, une réponse honnête. Vous repartez
            avec les deux ou trois endroits où votre entreprise pourrait
            simplifier d'abord — que vous travailliez avec nous ou non.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="mt-9 flex flex-col sm:flex-row sm:items-center gap-5"
          >
            <button
              data-testid="audit-cta-hero"
              onClick={() => setOpen(true)}
              className="inline-flex items-center gap-3 rounded-full bg-brand-navy text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-teal hover:-translate-y-0.5"
            >
              <Timer size={18} />
              Réserver mes 15 minutes
            </button>
            <p className="text-sm text-slate-500">Réponse personnelle en un jour ouvrable</p>
          </motion.div>
        </div>
      </section>

      {/* Minute par minute */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Minute par minute
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl max-w-2xl mb-14">
            Voilà exactement ce qui se passe.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-7">
            {MINUTES.map((m, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div data-testid={`audit-minute-${i}`} className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-7 sm:p-8 h-full transition-all duration-300 hover:border-brand-teal hover:-translate-y-0.5">
                  <span className="w-10 h-10 rounded-full bg-brand-tealsoft text-brand-teal flex items-center justify-center mb-4">
                    <m.icon size={17} />
                  </span>
                  <p className="font-mono text-xs uppercase tracking-[0.18em] text-brand-teal font-semibold mb-2">{m.range}</p>
                  <h3 className="font-display font-semibold text-xl text-brand-navy mb-3">{m.title}</h3>
                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">{m.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Ce que vous gardez */}
      <section className="py-16 lg:py-24 bg-brand-navy text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-gold font-semibold mb-5">
                Ce que vous gardez
              </p>
              <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-6">
                La carte de vos fuites, dans tous les cas.
              </h2>
              <p className="text-sm sm:text-base text-slate-300 leading-relaxed mb-6">
                Même si on ne travaille jamais ensemble, vous repartez avec une
                image claire : où vont les heures de votre équipe, dans quel
                ordre les récupérer, et ce que ça vaut. C'est notre façon de
                commencer chaque relation — par de la valeur, pas par un devis.
              </p>
              <div className="flex items-center gap-3 text-brand-gold">
                <Gift size={18} />
                <p className="font-display font-semibold text-lg">Gratuit. Sans relance. Sans piège.</p>
              </div>
            </div>
            <div className="rounded-2xl bg-brand-navysurf border border-white/10 p-7 sm:p-9">
              <p className="font-mono text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold mb-6">Après vos 15 minutes</p>
              <ul className="space-y-4">
                {[
                  "Vos 2 ou 3 premières simplifications, dans l'ordre",
                  "Le nombre d'heures que chacune pourrait rendre",
                  "Une réponse honnête : on peut aider, ou pas",
                  "Si ça clique : 30 jours d'essai gratuit sur vos vraies données",
                ].map((s, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm sm:text-base text-slate-300 leading-relaxed">
                    <span className="mt-0.5 w-5 h-5 rounded-full bg-brand-gold/15 text-brand-gold flex items-center justify-center font-mono text-[10px] font-bold shrink-0">
                      {i + 1}
                    </span>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-8">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-brand-teal font-semibold mb-5">
            Questions fréquentes
          </p>
          <h2 className="font-display font-bold tracking-tight text-brand-navy text-3xl sm:text-4xl mb-12">
            Ce qu'on nous demande avant de réserver
          </h2>
          <div className="space-y-4">
            {FAQ.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <details data-testid={`audit-faq-${i}`} className="group rounded-2xl bg-white border border-slate-200 px-7 py-6 open:border-brand-teal transition-colors">
                  <summary className="cursor-pointer list-none flex items-center justify-between gap-4 font-display font-semibold text-lg text-brand-navy">
                    {f.q}
                    <span className="text-brand-teal text-xl transition-transform duration-300 group-open:rotate-45">+</span>
                  </summary>
                  <p className="mt-4 text-sm sm:text-base text-slate-600 leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA final */}
      <section className="pb-20 lg:pb-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16">
          <Reveal>
            <div className="rounded-3xl bg-brand-navy text-white p-10 sm:p-14 flex flex-col lg:flex-row lg:items-center gap-8 justify-between">
              <div className="max-w-xl">
                <h2 className="font-display font-bold tracking-tight text-3xl sm:text-4xl leading-tight mb-4">
                  Quinze minutes maintenant. Des heures chaque semaine après.
                </h2>
                <p className="text-sm sm:text-base text-slate-300 leading-relaxed">
                  Les équipes avec qui on travaille récupèrent 25 à 40 % de
                  leur temps administratif — mesuré, pas promis. Ça commence
                  par un quart d'heure.
                </p>
              </div>
              <div className="flex flex-col items-start gap-4 shrink-0">
                <button
                  data-testid="audit-cta-final"
                  onClick={() => setOpen(true)}
                  className="inline-flex items-center gap-3 rounded-full bg-brand-teal text-white text-base font-semibold px-9 py-4 transition-all duration-300 hover:bg-brand-tealhover hover:-translate-y-0.5"
                >
                  Réserver mes 15 minutes
                  <ArrowRight size={18} />
                </button>
                <p className="text-xs text-slate-400">
                  Curieux de la méthode ?{" "}
                  <Link to="/fr/gardez-vos-outils" className="text-brand-gold hover:text-white transition-colors">
                    Gardez vos outils. On les connecte.
                  </Link>
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </main>
  );
}
